import { useMemo, useState } from "react";
import { useToast } from "@/components/base/Toast";
import { useStore } from "@/context/StoreContext";
import NotificationItem from "./components/NotificationItem";

const FILTERS: { key: string; label: string }[] = [
  { key: "all", label: "الكل" },
  { key: "unread", label: "غير مقروء" },
  { key: "delivery", label: "التوصيل" },
  { key: "financial", label: "مالية" },
  { key: "rating", label: "تقييمات" },
  { key: "store", label: "المتجر" },
  { key: "system", label: "نظامية" },
];

export default function NotificationsPage() {
  const { show } = useToast();
  const { notifications, markNotificationRead, markAllNotificationsRead } =
    useStore();
  const [filter, setFilter] = useState("all");

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.isRead).length,
    [notifications],
  );

  const counts = useMemo(() => {
    const result: Record<string, number> = {
      all: notifications.length,
      unread: unreadCount,
    };
    notifications.forEach((item) => {
      result[item.type] = (result[item.type] ?? 0) + 1;
    });
    return result;
  }, [notifications, unreadCount]);

  const visible = useMemo(() => {
    if (filter === "all") return notifications;
    if (filter === "unread") return notifications.filter((item) => !item.isRead);
    return notifications.filter((item) => item.type === filter);
  }, [notifications, filter]);

  const markRead = (id: string) => {
    markNotificationRead(id);
  };

  const markAllRead = () => {
    if (unreadCount === 0) {
      show("لا توجد إشعارات غير مقروءة", "warning");
      return;
    }
    markAllNotificationsRead();
    show("تم تعليم كل الإشعارات كمقروءة");
  };

  return (
    <div className="space-y-4">
      <section className="yc-fade-up flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-white rounded-card border border-background-200 shadow-card px-5 py-4">
        <div>
          <h2 className="text-base font-bold text-foreground-950">الإشعارات</h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            لديك {unreadCount} إشعار غير مقروء
          </p>
        </div>
        <button
          type="button"
          onClick={markAllRead}
          className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-background-100 px-5 text-sm font-semibold text-foreground-700 hover:bg-primary-100 hover:text-primary-800 transition-colors duration-200 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-check-double-line"></i>
          تعليم الكل كمقروء
        </button>
      </section>

      <section className="yc-fade-up flex flex-wrap gap-2">
        {FILTERS.map((item) => {
          const active = filter === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setFilter(item.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                active
                  ? "bg-primary-500 text-white"
                  : "bg-white border border-background-200 text-foreground-600 hover:bg-background-100"
              }`}
            >
              {item.label}
              <span
                className={`rounded-full px-1.5 text-[11px] font-bold ${
                  active
                    ? "bg-white/25 text-white"
                    : "bg-background-100 text-foreground-500"
                }`}
              >
                {counts[item.key] ?? 0}
              </span>
            </button>
          );
        })}
      </section>

      {visible.length === 0 ? (
        <section className="yc-fade-up bg-white rounded-card border border-background-200 shadow-card py-20 text-center">
          <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
            <i className="ri-notification-off-line text-2xl"></i>
          </div>
          <p className="text-sm text-foreground-500">
            لا توجد إشعارات في هذا القسم حاليًا
          </p>
        </section>
      ) : (
        <ul className="space-y-3">
          {visible.map((item) => (
            <NotificationItem key={item.id} {...item} onRead={markRead} />
          ))}
        </ul>
      )}
    </div>
  );
}